import { useApi, useToaster } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, toValue, watch } from "vue";

export function billingOptionsQueryKey(planId: number | null) {
  return ["admin", "expert-plans", "billing-options", planId] as const;
}

/**
 * Loads the billing options of one plan. Keyed under the shared
 * ["admin", "expert-plans"] root so plan-level invalidations refresh
 * option lists too.
 */
export function useAdminPlanBillingOptions(planId: MaybeRefOrGetter<number | null>) {
  const api = useApi();
  const toaster = useToaster();

  const planIdValue = computed(() => toValue(planId));

  const query = useQuery({
    queryKey: computed(() => billingOptionsQueryKey(planIdValue.value)),
    queryFn: async () => {
      return await api.admin.expertBilling.listBillingOptions({
        params: { plan: planIdValue.value as number },
      });
    },
    enabled: computed(() => planIdValue.value !== null),
    staleTime: 30 * 1000,
    retry: 1,
  });

  watch(query.error, (err) => {
    if (!err) return;
    toaster.create({
      title: "Failed to load billing options",
      description: err.message,
      type: "error",
    });
    console.error("[useAdminPlanBillingOptions] Error:", err);
  });

  const options = computed(() => query.data.value ?? []);

  return {
    options,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export default useAdminPlanBillingOptions;
